import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartItemsComponent } from '@theme/component-cart-items';
import { ThemeEvents } from '@theme/events';

// One module and one shared lock for both cart contexts and all purchase sections.
let adding = false;
let uncertain = false;
const pending = new Set();
let quantityDebounceUntil = 0;
const root = () => window.Shopify.routes.root;
const busy = () => adding || pending.size > 0 || Date.now() < quantityDebounceUntil;
const nextPaint = () => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

async function jsonFetch(url, options = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.method === 'POST' ? 25000 : 15000);
  try {
    const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'No pudimos actualizar el carrito. Intentá de nuevo.');
    return data;
  } finally { clearTimeout(timeout); }
}
const readCart = () => jsonFetch(`${root()}cart.js`, { cache: 'no-store' });

function paintBusy() {
  document.documentElement.toggleAttribute('data-pdc-cart-busy', busy());
  document.querySelectorAll('pdc-purchase').forEach(el => el.updateBusy?.());
  document.querySelectorAll('.cart__ctas').forEach(el => {
    el.inert = busy() || uncertain;
    el.setAttribute('aria-busy', String(busy() || uncertain));
  });
}
function message(text, isError = false) {
  document.querySelectorAll('[data-pdc-status]').forEach(node => {
    node.textContent = text;
    node.setAttribute('role', isError ? 'alert' : 'status');
  });
}
async function openCart() {
  let drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open !== 'function' && typeof customElements.whenDefined === 'function') {
    let timeout;
    try {
      await Promise.race([
        customElements.whenDefined('theme-drawer'),
        new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('drawer timeout')), 6000); })
      ]);
      drawer = document.getElementById('cart-drawer');
    } catch { /* Fall back only after allowing the native component to upgrade. */ }
    finally { clearTimeout(timeout); }
  }
  if (typeof drawer?.open === 'function') drawer.open();
  else window.location.assign(document.body?.dataset?.pdcCartUrl || `${root()}cart`);
}

// Preserve native checkout submission; only stop it while a mutation or refresh is unresolved.
function guardCheckout(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const checkout = event.type === 'submit'
    ? (event.submitter?.name === 'checkout' || target.id === 'cart-form')
    : target.closest('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons');
  if (checkout && (busy() || uncertain)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    message(uncertain ? 'Necesitamos actualizar el carrito antes de continuar. Usá «Actualizar carrito».' : 'Esperá un momento mientras actualizamos tu carrito.');
  }
}
document.addEventListener('click', guardCheckout, true);
document.addEventListener('submit', guardCheckout, true);
// Prevent a competing native line mutation during this module's transaction.
document.addEventListener('click', event => {
  if (!adding || !(event.target instanceof Element)) return;
  if (event.target.closest('cart-items-component') && event.target.closest('quantity-selector-component, .cart-items__remove, [on\\:click*="onLineItemRemove"]')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);
document.addEventListener(ThemeEvents.quantitySelectorUpdate, event => {
  if (adding) { event.stopImmediatePropagation(); return; }
  quantityDebounceUntil = Date.now() + 350;
  paintBusy();
  setTimeout(paintBusy, 360);
}, true);
document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (!event.promise) return;
  pending.add(event.promise);
  paintBusy();
  event.promise.then(({cart}) => {
    if (cart) document.querySelectorAll('[data-pdc-count]').forEach(el => { el.textContent = cart.totalQuantity; });
  }).catch(() => {}).finally(async () => {
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    await nextPaint();
    pending.delete(event.promise);
    paintBusy();
  });
});

async function cartSections(bundled = {}) {
  const contexts = [...document.querySelectorAll('cart-items-component[data-section-id]')];
  const ids = [...new Set(contexts.map(el => el.dataset.sectionId).filter(Boolean))];
  if (!ids.length) throw new Error('No pudimos cargar el carrito. Actualizá la página.');
  const sections = { ...bundled };
  // An add can succeed while bundled rendering returns null. Retry only rendering, never the POST.
  const missing = ids.filter(id => !sections[id]);
  for (let i = 0; i < missing.length; i += 5) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', missing.slice(i, i + 5).join(','));
    const rendered = await jsonFetch(url.toString(), { cache: 'no-store' });
    Object.assign(sections, rendered);
  }
  for (const id of ids) {
    const html = sections[id];
    if (!html || !new DOMParser().parseFromString(html, 'text/html').querySelector('cart-items-component')) {
      throw new Error('Tu carrito necesita actualizarse antes de continuar.');
    }
    sectionRenderer.abortRender(id);
    const isDrawer = contexts.find(el => el.dataset.sectionId === id)?.hasAttribute('data-drawer');
    await morphSection(id, html, { mode: isDrawer ? 'hydration' : 'full', injectStylesheet: true });
  }
  await nextPaint();
  return sections;
}

function updateAdded(cart) {
  document.querySelectorAll('pdc-purchase[data-complement]').forEach(el => {
    el.toggleAttribute('data-added', cart.items.some(item => String(item.product_id) === el.dataset.product));
    el.updateBusy?.();
  });
  document.querySelectorAll('[data-pdc-count]').forEach(el => { el.textContent = cart.item_count; });
}
function dispatchCart(cart, sections, variantId, action = 'add', didError = false) {
  const result = {
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { source: 'pdc-cart', sectionsApplied: true, sections, items: cart.items, didError }
  };
  if (action === 'update' && !cart.items.length) return;
  document.dispatchEvent(new CartLinesUpdateEvent({
    action, context: 'cart',
    lines: action === 'add' ? [{ merchandiseId: variantId, quantity: 1 }] : cart.items.map(item => ({ id: item.key, quantity: item.quantity })),
    promise: Promise.resolve(result), detail: { source: 'pdc-cart' }
  }));
}

class PdcPurchase extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.refreshRefs();
    // Listen on the stable custom element: Horizon may morph its inner form without
    // reconnecting this element. A listener on the old form would leave the new one
    // doing an ordinary HTML POST (and navigating to the full cart).
    this.addEventListener('submit', event => {
      if (event.target === this.querySelector('form')) this.submit(event);
    }, { signal });
    this.addEventListener('change', event => {
      const select = this.querySelector('select');
      if (event.target !== select) return;
      const price = select.selectedOptions[0]?.dataset.price;
      const node = this.querySelector('[data-price]');
      if (node) node.textContent = price || 'Elegí una opción para ver el precio';
    }, { signal });
    this.updateBusy();
  }
  disconnectedCallback() { this.abort?.abort(); }
  refreshRefs() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.feedback = this.querySelector('.pdc-feedback');
    this.originalLabel = this.button?.dataset.pdcLabel || this.button?.textContent.trim();
    this.unavailable = this.hasAttribute('data-unavailable');
  }
  updateBusy() {
    this.refreshRefs();
    if (!this.button) return;
    const added = this.hasAttribute('data-added');
    this.button.disabled = this.unavailable || added || busy() || uncertain;
    if (!this.unavailable) this.button.textContent = added ? 'Agregado al carrito' : (adding ? 'Actualizando…' : this.originalLabel);
    this.setAttribute('aria-busy', String(adding));
  }
  async submit(event) {
    event.preventDefault();
    this.refreshRefs();
    if (busy() || uncertain || this.unavailable || this.hasAttribute('data-added')) return;
    if (!this.form.reportValidity()) return;
    const id = new FormData(this.form).get('id');
    if (!id) return;
    adding = true;
    paintBusy();
    this.feedback.setAttribute('role', 'status');
    this.feedback.textContent = 'Actualizando tu carrito…';
    let attempted = false;
    let confirmed = false;
    try {
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
      let cart = await readCart();
      // Product-level check prevents another variant of the same optional PDF being added twice.
      if (cart.items.some(item => String(item.product_id) === this.dataset.product)) {
        await cartSections();
        updateAdded(cart);
        dispatchCart(cart, {}, id, 'update');
        if (!this.hasAttribute('data-complement')) await openCart();
        this.feedback.textContent = 'Ya está en tu carrito.';
        return;
      }
      const ids = [...new Set([...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
      attempted = true;
      const result = await jsonFetch(`${root()}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: ids, sections_url: window.location.pathname })
      });
      confirmed = true;
      cart = await readCart();
      if (!cart.items.some(item => String(item.variant_id) === String(id))) throw new Error('No pudimos confirmar el producto en el carrito.');
      const sections = await cartSections(result.sections);
      updateAdded(cart);
      dispatchCart(cart, sections, id);
      message('Producto agregado al carrito.');
      this.feedback.textContent = 'Producto agregado al carrito.';
      if (!this.hasAttribute('data-complement')) await openCart();
    } catch (error) {
      // A lost POST response is ambiguous: reconcile before enabling a retry.
      // Never automatically repeat a cart mutation.
      try {
        const cart = await readCart();
        const sections = await cartSections();
        updateAdded(cart);
        dispatchCart(cart, sections, id, 'update', true);
        const present = cart.items.some(item => String(item.variant_id) === String(id));
        if (attempted && present) {
          const text = 'El producto está en tu carrito. Revisalo antes de continuar.';
          message(text);
          this.feedback.textContent = text;
          if (!this.hasAttribute('data-complement')) await openCart();
        } else {
          const text = confirmed ? 'No pudimos confirmar el agregado. Revisá el carrito antes de continuar.' : (error.message || 'No pudimos agregar el producto. Intentá de nuevo.');
          this.feedback.textContent = text;
          this.feedback.setAttribute('role', 'alert');
          message(text, true);
        }
      } catch {
        uncertain = true;
        const text = 'No pudimos confirmar el estado del carrito. Actualizalo antes de intentar de nuevo.';
        this.feedback.textContent = text;
        this.feedback.setAttribute('role', 'alert');
        message(text, true);
        document.querySelectorAll('[data-pdc-reload]').forEach(el => el.removeAttribute('hidden'));
      }
    } finally {
      adding = false;
      paintBusy();
    }
  }
}
if (!customElements.get('pdc-purchase')) customElements.define('pdc-purchase', PdcPurchase);
// Native details work without JS; close the small mobile menu after anchor navigation.
document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  event.target.closest('.pdc-mobile-menu a')?.closest('details')?.removeAttribute('open');
  if (event.target.closest('[data-pdc-buy-link], .pdc-editorial a[href="#comprar"]')) {
    const purchase = document.querySelector('.pdc-hero:not(.pdc-recap) pdc-purchase:not([data-complement])') || document.querySelector('.pdc-hero pdc-purchase:not([data-complement])');
    if (purchase?.form) {
      event.preventDefault();
      const select = purchase.querySelector('select');
      if (select && !select.value) {
        purchase.scrollIntoView({ block: 'center' });
        select.focus({ preventScroll: true });
      } else purchase.form.requestSubmit();
    }
  }
  if (event.target.closest('.pdc-cart-link') && document.getElementById('cart-drawer')) {
    event.preventDefault(); openCart();
  }
});
